/**
 * Clinicians Page
 *
 * Page for managing organization members and invitations.
 * Part of the "Your Practice" feature.
 */

import { useCallback, useEffect, useState } from 'react';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { JotRxSetupCallout } from '@features/eprescriptions';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { InviteUserDialog } from './components/InviteUserDialog';
import { MemberTable } from './components/MemberTable';
import { PendingInvitationsSection } from './components/PendingInvitationsSection';
import { PermissionsMatrix } from './components/PermissionsMatrix';
import {
  useGetAvailableRolesQuery,
  useGetInvitationsQuery,
  useGetOrganizationMembersQuery,
  useOrganizationAccess,
  useTransformInvitations,
  useTransformMembers,
} from './store';

export default function CliniciansPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { clinicians } = useEventTracking();

  // State
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [autoEditUserId, setAutoEditUserId] = useState<string | null>(null);

  // Organization access
  const { userState, userIsInGroup, userIsBillingAdmin, organizationData, refetchOrg } =
    useOrganizationAccess();

  // API Queries
  const {
    data: membersData,
    isLoading: membersLoading,
    refetch: refetchMembers,
  } = useGetOrganizationMembersQuery(undefined, {
    skip: !userIsInGroup,
  });

  const { data: invitationsData, refetch: refetchInvitations } = useGetInvitationsQuery(undefined, {
    skip: !userIsInGroup || !userIsBillingAdmin,
  });

  const { data: rolesData } = useGetAvailableRolesQuery(undefined, {
    skip: !userIsInGroup,
  });

  // Transform data
  const { activeMembers, inactiveMembers } = useTransformMembers(membersData?.members);
  const invitations = useTransformInvitations(invitationsData?.invitations);

  // Track page view on mount
  useEffect(() => {
    if (membersData && invitationsData) {
      clinicians.pageViewed({
        member_count: membersData.members?.length || 0,
        active_member_count: activeMembers.length,
        pending_invitation_count: invitations.length,
      });
    }
  }, [membersData, invitationsData, activeMembers.length, invitations.length, clinicians]);

  // Callbacks
  const handleRefreshAll = useCallback(async () => {
    clinicians.refreshClicked();
    try {
      await Promise.all([refetchOrg(), refetchMembers(), refetchInvitations()]);
      enqueueSnackbar('Data refreshed successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    }
  }, [refetchOrg, refetchMembers, refetchInvitations, enqueueSnackbar]);

  const toggleRowExpanded = useCallback(
    (userId: string) => {
      setExpandedRows(prev => {
        const next = new Set(prev);
        const isExpanding = !next.has(userId);
        if (isExpanding) {
          next.add(userId);
          // Find member to get metadata
          const member = [...activeMembers, ...inactiveMembers].find(m => m.userId === userId);
          if (member) {
            clinicians.memberRowExpanded({
              member_id: userId,
              role_count: member.roles.length,
              has_bundles: (member.bundles?.length || 0) > 0,
            });
          }
        } else {
          next.delete(userId);
        }
        return next;
      });
    },
    [activeMembers, inactiveMembers, clinicians],
  );

  const handleInviteSuccess = useCallback(() => {
    setInviteDialogOpen(false);
    refetchInvitations();
    // Note: inviteSent event is tracked in InviteUserDialog component
  }, [refetchInvitations]);

  const handleMemberChange = useCallback(() => {
    refetchMembers();
  }, [refetchMembers]);

  const handleEditCurrentUser = useCallback(() => {
    if (userState?.user_id) {
      setAutoEditUserId(userState.user_id);
    }
  }, [userState?.user_id]);

  const handleAutoEditHandled = useCallback(() => {
    setAutoEditUserId(null);
  }, []);

  return (
    <>
      {/* JotRx Setup Callout */}
      <JotRxSetupCallout page="clinicians" onAction={handleEditCurrentUser} />

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <Button
          variant="contained"
          onClick={() => {
            clinicians.inviteDialogOpened();
            setInviteDialogOpen(true);
          }}
        >
          Invite User
        </Button>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefreshAll}>
          Refresh
        </Button>
      </Box>

      <Box id="member-table" sx={{ display: 'flex', flexDirection: 'column', gap: 4, mb: 6 }}>
        {/* Active Members Table */}
        <MemberTable
          title="Active Members"
          members={activeMembers}
          expandedRows={expandedRows}
          onToggleExpand={toggleRowExpanded}
          onMemberChange={handleMemberChange}
          availableRoles={rolesData?.roles}
          autoEditUserId={autoEditUserId}
          onAutoEditHandled={handleAutoEditHandled}
        />

        {/* Pending Invitations */}
        {invitations.length > 0 && (
          <PendingInvitationsSection
            invitations={invitations}
            onInvitationChange={refetchInvitations}
          />
        )}

        {/* Inactive Members Section */}
        {inactiveMembers.length > 0 && (
          <MemberTable
            title={`Inactive Members (${inactiveMembers.length})`}
            members={inactiveMembers}
            expandedRows={expandedRows}
            onToggleExpand={toggleRowExpanded}
            onMemberChange={handleMemberChange}
            availableRoles={rolesData?.roles}
            isInactive
            collapsible
            defaultCollapsed
          />
        )}

        {/* Permissions Matrix */}
        <PermissionsMatrix />
      </Box>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSuccess={handleInviteSuccess}
        availableRoles={rolesData?.roles}
        isTrialing={organizationData?.subscription_status === 'trialing'}
      />
    </>
  );
}
